import { useState } from 'react';
import PropTypes from 'prop-types';
import Sketch from '@uiw/react-color-sketch';
import Swatch from '@uiw/react-color-swatch';
import {
  Typography,
  FormHelperText,
  Popover,
  alpha,
  makeStyles,
} from '@material-ui/core';
import Tooltip from '../tooltip';
import ColorBox from '../colorBox';
import { COLOR_PALETTE } from '../../../constants/colorPalette';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  labelWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  tooltipIcon: {
    marginLeft: spacing(1),
    width: 14,
    height: 14,
  },
  pickerButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  colorBoxButton: {
    marginRight: spacing(2),
  },
  popover: {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    marginTop: spacing(2),
    overflowX: 'visible',
    overflowY: 'visible',
  },
  swatchWrapper: {
    boxSizing: 'border-box',
    backgroundColor: primary.white,
    border: `1px solid ${primary.bluish6}`,
    borderRadius: 4,
    boxShadow: `0px 2px 8px 0px ${alpha(primary.black, 0.07)}`,
    padding: spacing(2.5),
    width: 142,
    '& .swatch': {
      gap: 6,
      '& div': {
        boxSizing: 'border-box',
        border: `1px solid ${primary.bluish1}`,
        marginRight: '0 !important',
        marginBottom: '0 !important',
      },
    },
  },
}));

const ColorPicker = ({
  className = undefined,
  label,
  tooltipText,
  selectedColor = '',
  onColorPick,
  collorPalette = null,
  shouldDisableCustomColorPick = false,
}) => {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeSketchColor = ({ hex }) => {
    if (hex !== selectedColor) {
      onColorPick(hex);
    }
  };

  const handleChangeSwatchColor = (_, { hex }) => {
    if (hex !== selectedColor) {
      onColorPick(hex);
    }
  };

  return (
    <div className={className}>
      <FormHelperText>
        <span>{label}</span>
        <Tooltip customIconClass={classes.tooltipIcon} text={tooltipText} />
      </FormHelperText>
      <div className={classes.pickerButtonWrapper} onClick={handleOpen}>
        <ColorBox
          className={classes.colorBoxButton}
          bgColor={selectedColor}
          isLarge
        />
        <Typography variant="body2">{selectedColor}</Typography>
      </div>
      <Popover
        anchorEl={anchorEl}
        open={!!anchorEl}
        PaperProps={{
          classes: { root: classes.popover },
        }}
        onClose={handleClose}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        keepMounted
      >
        {shouldDisableCustomColorPick ? (
          <div className={classes.swatchWrapper}>
            <Swatch
              prefixCls="swatch"
              colors={collorPalette || COLOR_PALETTE}
              color={selectedColor}
              onChange={handleChangeSwatchColor}
            />
          </div>
        ) : (
          <Sketch
            color={selectedColor}
            presetColors={collorPalette || COLOR_PALETTE}
            onChange={handleChangeSketchColor}
            disableAlpha
          />
        )}
      </Popover>
    </div>
  );
};

ColorPicker.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  tooltipText: PropTypes.string.isRequired,
  selectedColor: PropTypes.string,
  onColorPick: PropTypes.func.isRequired,
  collorPalette: PropTypes.arrayOf(PropTypes.string),
  shouldDisableCustomColorPick: PropTypes.bool,
};

export default ColorPicker;
